import { supabase } from '../lib/supabase';
import { testTimezoneHandling } from './testTimezone';

// Define types
interface ReminderData {
  id: string;
  booking_id: string;
  automation_id: string;
  reminder_hours: number;
  target_time: string;
  sent_at: string;
  bookings?: {
    id: string;
    customer_id: string;
    status: string;
    drop_off_date: string;
    drop_off_time: string;
    service_name?: string;
    businesses?: {
      id: string;
      name: string;
    };
    customers?: {
      id: string;
      first_name: string;
      last_name: string;
      email?: string;
      phone?: string;
    };
  };
  automation_settings?: {
    id: string;
    sms_action_active: boolean;
    sms_action_message?: string;
    email_action_active: boolean;
    email_action_subject?: string;
    email_action_body?: string;
  };
}

// Fetch recently processed reminders that haven't been handled by automations
export async function fetchRecentlyProcessedReminders() {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }
  
  // Increase time window to last 30 minutes for debugging
  const timeWindow = new Date(Date.now() - 30 * 60 * 1000).toISOString();
  console.log('Checking for reminders processed since:', timeWindow);
  
  // Get the list of IDs we've already processed
  const processedIds = getProcessedReminderIds();
  
  try {
    // Modified query that avoids the not-in filter which is causing errors
    // Also removed service_name field which might not exist
    const { data, error } = await supabase
      .from('booking_reminders_due')
      .select(`
        id,
        booking_id,
        automation_id,
        reminder_hours,
        target_time,
        sent_at,
        bookings:booking_id (
          id,
          customer_id,
          status,
          drop_off_date,
          drop_off_time,
          businesses:business_id (
            id,
            name
          ),
          customers:customer_id (
            id,
            first_name,
            last_name,
            email,
            phone
          )
        ),
        automation_settings:automation_id (
          id,
          sms_action_active,
          sms_action_message,
          email_action_active,
          email_action_subject,
          email_action_body
        )
      `)
      .eq('reminder_sent', true)
      .gt('sent_at', timeWindow)
      .order('sent_at', { ascending: true });
      
    if (error) {
      console.error('Error fetching processed reminders:', error);
      return [];
    }
    
    // Filter out already processed reminders in JavaScript instead of in the query
    const filteredData = data?.filter(reminder => !processedIds.includes(reminder.id)) || [];
    
    // Log detailed results for debugging
    console.log(`Found ${filteredData.length} unprocessed reminders:`);
    if (filteredData.length > 0) {
      filteredData.forEach(reminder => {
        console.log(`Reminder ID: ${reminder.id}, Booking ID: ${reminder.booking_id}, Sent At: ${reminder.sent_at}`);
      });
    } else {
      console.log('No unprocessed reminders found');
    }
    
    return filteredData;
  } catch (err) {
    console.error('Exception in fetchRecentlyProcessedReminders:', err);
    return [];
  }
}

// Keep track of reminders we've already processed
function getProcessedReminderIds(): string[] {
  try {
    const stored = localStorage.getItem('processed_reminder_ids');
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (e) {
    return [];
  }
}

function addProcessedReminderId(id: string) {
  try {
    const ids = getProcessedReminderIds();
    const newIds = [...ids, id];
    // Keep only the most recent 100 IDs to avoid growing forever
    const limitedIds = newIds.slice(-100);
    localStorage.setItem('processed_reminder_ids', JSON.stringify(limitedIds));
  } catch (e) {
    console.error('Error saving processed reminder ID:', e);
  }
}

// Function to format time in AM/PM format
function formatTimeAMPM(timeStr: string): string {
  if (!timeStr) return '';
  
  try {
    // Handle different time formats
    let hours: number;
    let minutes: number;
    
    // Handle formats like "13:30:00" or "13:30"
    if (timeStr.includes(':')) {
      const [hoursStr, minutesStr] = timeStr.split(':');
      hours = parseInt(hoursStr, 10);
      minutes = parseInt(minutesStr, 10);
    } 
    // Handle format like "1330"
    else if (timeStr.length === 4) {
      hours = parseInt(timeStr.substring(0, 2), 10);
      minutes = parseInt(timeStr.substring(2), 10);
    }
    // Default case
    else {
      return timeStr; // Return original if we can't parse
    }
    
    // Convert to 12-hour format
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours || 12; // Convert 0 to 12
    
    // Format the result
    return `${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}${ampm}`;
  } catch (e) {
    console.error('Error formatting time:', e);
    return timeStr; // Return original on error
  }
}

// Process a single reminder
export async function processReminder(reminderData: any) {
  try {
    // IMPORTANT: First, check if this specific reminder is being processed right now
    // This handles cases where the same reminder might be processed twice simultaneously
    const processingKey = `processing_reminder_${reminderData.id}`;
    if (sessionStorage.getItem(processingKey)) {
      console.log(`🔒 ACTIVE LOCK: Reminder ${reminderData.id} is currently being processed elsewhere, skipping`);
      return { success: true, skipped: true, reason: 'active_lock' };
    }
    
    // Set a processing lock with an expiry timestamp (5 minutes)
    const lockExpiry = Date.now() + 5 * 60 * 1000;
    sessionStorage.setItem(processingKey, lockExpiry.toString());
    
    // Also check localStorage for already processed reminders
    const processedIds = getProcessedReminderIds();
    if (processedIds.includes(reminderData.id)) {
      console.log(`🔒 ALREADY PROCESSED: Reminder ${reminderData.id} already processed, skipping duplicates`);
      // Release the lock since we're skipping
      sessionStorage.removeItem(processingKey);
      return { success: true, skipped: true, reason: 'already_processed' };
    }

    // Mark this reminder as processed immediately to prevent duplicate processing
    // This is especially important if there are multiple browser tabs/windows open
    addProcessedReminderId(reminderData.id);
    
    // Create a unique deduplication key for this reminder processing attempt
    const now = new Date();
    const minuteWindow = `${now.getFullYear()}-${now.getMonth()+1}-${now.getDate()}-${now.getHours()}-${now.getMinutes()}`;
    const dedupeKey = `reminder_${reminderData.id}_${minuteWindow}`;
    
    // Check if this reminder was processed in the same minute window
    if (localStorage.getItem(dedupeKey)) {
      console.log(`🔒 MINUTE WINDOW: Reminder ${reminderData.id} processed in the same minute window, skipping`);
      // Release the lock since we're skipping
      sessionStorage.removeItem(processingKey);
      return { success: true, skipped: true, reason: 'minute_window' };
    }
    
    // Store in localStorage to ensure we don't process in the same minute window
    localStorage.setItem(dedupeKey, 'processing');
    
    // From this point on, we're committed to processing the reminder
    console.log(`🔓 LOCK ACQUIRED: Processing reminder ${reminderData.id} with lock ${processingKey}`);
    
    // Convert API data format to our interface format
    const reminder: ReminderData = {
      id: reminderData.id,
      booking_id: reminderData.booking_id,
      automation_id: reminderData.automation_id,
      reminder_hours: reminderData.reminder_hours,
      target_time: reminderData.target_time,
      sent_at: reminderData.sent_at,
      bookings: reminderData.bookings ? {
        id: reminderData.bookings.id,
        customer_id: reminderData.bookings.customer_id,
        status: reminderData.bookings.status,
        drop_off_date: reminderData.bookings.drop_off_date,
        drop_off_time: reminderData.bookings.drop_off_time,
        service_name: reminderData.bookings.service_name || 'Appointment', // Use default if not present
        businesses: reminderData.bookings.businesses,
        customers: reminderData.bookings.customers
      } : undefined,
      automation_settings: reminderData.automation_settings
    };
    
    // Test timezone handling with the booking date
    if (reminder.bookings?.drop_off_date) {
      console.log('📅 TIMEZONE TEST FOR BOOKING DATE:');
      const tzResults = testTimezoneHandling(reminder.bookings.drop_off_date);
      console.log('Current implementation would use:', tzResults.localFormatted);
      console.log('UTC implementation would use:', tzResults.utcFormatted);
    }
    
    // Log detailed reminder data for debugging
    console.log('Processing reminder with data:', {
      id: reminder.id,
      booking_id: reminder.booking_id,
      automation_id: reminder.automation_id,
      booking_status: reminder.bookings?.status,
      customer: reminder.bookings?.customers ? {
        name: `${reminder.bookings.customers.first_name} ${reminder.bookings.customers.last_name}`,
        email: reminder.bookings.customers.email,
        phone: reminder.bookings.customers.phone
      } : null,
      automation: reminder.automation_settings ? {
        sms_active: reminder.automation_settings.sms_action_active,
        email_active: reminder.automation_settings.email_action_active
      } : null
    });
    
    if (!reminder.bookings || !reminder.automation_settings) {
      console.error('Missing booking or automation data');
      // Release the lock
      sessionStorage.removeItem(processingKey);
      return { success: false };
    }
    
    const booking = reminder.bookings;
    const automation = reminder.automation_settings;
    const customer = booking.customers;
    const business = booking.businesses;
    
    // Skip if no customer data
    if (!customer) {
      console.error('No customer data for booking', booking.id);
      // Release the lock
      sessionStorage.removeItem(processingKey);
      return { success: false };
    }
    
    // Skip if booking is not scheduled
    if (booking.status !== 'scheduled') {
      console.log('Skipping reminder for non-scheduled booking', booking.id);
      // Release the lock
      sessionStorage.removeItem(processingKey);
      return { success: false };
    }
    
    // Check if a reminder was already sent for this booking/hours combination
    // This adds an extra layer of deduplication
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
      } else {
        const { data: existingActions } = await supabase
          .from('automation_action_log')
          .select('id, created_at')
          .eq('business_id', business?.id || '')
          .eq('recipient', customer.phone || customer.email || '')
          .contains('trigger_data', { booking_id: booking.id, reminder_hours: reminder.reminder_hours })
          .order('created_at', { ascending: false })
          .limit(1);
          
        if (existingActions && existingActions.length > 0) {
          // Calculate time since the last action for this booking/hours combination
          const lastActionTime = new Date(existingActions[0].created_at).getTime();
          const currentTime = new Date().getTime();
          const minutesSinceLastAction = (currentTime - lastActionTime) / (1000 * 60);
          
          // If we already sent a reminder for this booking within the last 5 minutes, skip
          if (minutesSinceLastAction < 5) {
            console.log(`🔒 DATABASE CHECK: Skipping duplicate reminder - last action was only ${minutesSinceLastAction.toFixed(1)} minutes ago`);
            // Release the lock
            sessionStorage.removeItem(processingKey);
            return { success: true, skipped: true, reason: 'recent_duplicate' };
          } else {
            console.log(`Found existing action but it's ${minutesSinceLastAction.toFixed(1)} minutes old, proceeding`);
          }
        }
      }
    } catch (error) {
      // If the check fails, continue with processing (fail open)
      console.error('Error checking for existing actions:', error);
    }
    
    // Prepare template data
    const templateData: Record<string, string> = {
      customer_first_name: customer.first_name || '',
      customer_last_name: customer.last_name || '',
      customer_full_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      drop_off_date_formatted: booking.drop_off_date 
        ? new Date(booking.drop_off_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      drop_off_time_formatted: formatTimeAMPM(booking.drop_off_time || ''),
      business_name: business?.name || '',
      appointment_id: booking.id,
      service_name: booking.service_name || '',
      hours_until_appointment: String(reminder.reminder_hours),
      drop_off_date_raw: booking.drop_off_date || '',
      target_time_raw: reminder.target_time || ''
    };
    
    // Log the dates for debugging
    console.log('Date values for reminder:', {
      reminder_id: reminder.id,
      booking_id: booking.id,
      drop_off_date: booking.drop_off_date,
      drop_off_date_obj: booking.drop_off_date ? new Date(booking.drop_off_date) : null,
      drop_off_date_formatted: templateData.drop_off_date_formatted,
      target_time: reminder.target_time,
      target_time_obj: reminder.target_time ? new Date(reminder.target_time) : null,
      current_time: new Date().toISOString(),
      timezone_offset: new Date().getTimezoneOffset(),
      timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    // Explicitly log what date is being used in messages
    console.log('⚠️ IMPORTANT: Messages will reference date:', templateData.drop_off_date_formatted);
    
    // Import the actions for direct execution
    const { SMS_ACTION, EMAIL_ACTION } = await import('../lib/automations/actions');
    
    const results = [];
    
    // Process SMS action
    if (automation.sms_action_active && customer.phone) {
      try {
        // Set the message on the action instance
        SMS_ACTION.settings = {
          ...SMS_ACTION.settings,
          message: automation.sms_action_message || ''
        };
        
        // Replace template variables (this is now handled in the action itself)
        const triggerData = {
          ...templateData,
          phoneNumber: customer.phone,
          email: customer.email,
          booking_id: booking.id,
          reminder_hours: reminder.reminder_hours,
          reminder_id: reminder.id,
          processing_id: dedupeKey // Add the deduplication key for extra safety
        };
        
        // Execute the SMS action
        const businessId = business?.id || '';
        console.log('Executing SMS action with trigger data:', triggerData);
        await SMS_ACTION.execute(businessId, triggerData);
        
        results.push({ type: 'sms', success: true });
      } catch (error) {
        console.error('Error executing SMS action:', error);
        results.push({ type: 'sms', success: false, error });
      }
    }
    
    // Process Email action
    if (automation.email_action_active && customer.email) {
      try {
        // Set the email template on the action instance
        EMAIL_ACTION.settings = {
          ...EMAIL_ACTION.settings,
          subject: automation.email_action_subject || '',
          emailBody: automation.email_action_body || ''
        };
        
        // Replace template variables (this is now handled in the action itself)
        const triggerData = {
          ...templateData,
          phoneNumber: customer.phone,
          email: customer.email,
          booking_id: booking.id,
          reminder_hours: reminder.reminder_hours,
          reminder_id: reminder.id,
          processing_id: dedupeKey // Add the deduplication key for extra safety
        };
        
        // Execute the Email action
        const businessId = business?.id || '';
        console.log('Executing Email action with trigger data:', triggerData);
        await EMAIL_ACTION.execute(businessId, triggerData);
        
        results.push({ type: 'email', success: true });
      } catch (error) {
        console.error('Error executing Email action:', error);
        results.push({ type: 'email', success: false, error });
      }
    }
    
    // Clean up the deduplication key
    sessionStorage.removeItem(processingKey);
    localStorage.setItem(dedupeKey, 'completed');
    
    console.log(`✅ Successfully processed reminder ${reminder.id} for booking ${booking.id}`, {
      results,
      customer: `${customer.first_name} ${customer.last_name}`,
      appointment: booking.drop_off_date ? `${booking.drop_off_date} ${booking.drop_off_time}` : 'No date set'
    });
    
    return { success: true, results };
  } catch (error) {
    console.error('Error processing reminder:', error);
    // Make sure to release the lock even on error
    try {
      const processingKey = `processing_reminder_${reminderData.id}`;
      sessionStorage.removeItem(processingKey);
    } catch (lockError) {
      console.error('Error releasing lock:', lockError);
    }
    return { success: false, error };
  }
}

// Process all pending reminders
export async function processAllReminders() {
  const reminders = await fetchRecentlyProcessedReminders();
  console.log(`Found ${reminders.length} reminders to process`);
  
  const results = [];
  
  for (const reminder of reminders) {
    const result = await processReminder(reminder);
    results.push({ reminderId: reminder.id, ...result });
  }
  
  return { processed: results.length, results };
} 